<template>
  <section
    id="portfolio"
    ref="targetElement"
    class="py-16 sm:py-20 lg:py-20 xl:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
    aria-labelledby="portfolio-heading"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div
        class="text-center mb-12 sm:mb-16 lg:mb-12 xl:mb-16 transition-all duration-700 ease-out"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
      >
        <h2
          class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6"
          id="portfolio-heading"
        >
          Umgesetzte Projekte
        </h2>
        <div
          class="mt-4 h-0.5 w-16 bg-blue-600 dark:bg-blue-400 mx-auto"
          aria-hidden="true"
        ></div>
        <p class="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
          So kann eine Website oder Weblösung aussehen, wenn sie auf konkrete Ziele und Arbeitsabläufe zugeschnitten ist.
        </p>
        </div>
      </div>

      <div v-if="loading" class="text-center py-16 sm:py-20" role="status" aria-live="polite">
        <div class="inline-block w-12 h-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mb-4" aria-hidden="true"></div>
        <p class="text-gray-600 dark:text-gray-400 text-sm sm:text-base font-medium">
          Projekte werden geladen...
        </p>
      </div>

      <div v-else-if="displayProjects.length === 0" class="text-center py-16 sm:py-20" role="status">
        <div class="max-w-md mx-auto">
          <div class="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center" aria-hidden="true">
            <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <p class="text-gray-600 dark:text-gray-400 mb-6 text-base sm:text-lg">
            Noch keine favorisierten Projekte vorhanden.
          </p>
          <router-link
            to="/contact"
            class="inline-flex items-center px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/30 dark:shadow-blue-600/30"
            aria-label="Zur Kontaktseite"
          >
            Werden Sie mein erstes Projekt
            <svg class="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
        </div>
      </div>

      <div v-else>
        <div
          class="grid gap-4 sm:gap-5 lg:gap-6 min-[1980px]:hidden"
          :class="favoriteGridClasses"
          role="list"
          aria-labelledby="portfolio-heading"
        >
          <PortfolioItem
            v-for="(project, index) in visibleProjects"
            :key="`favorite-${project.id || project.title}`"
            :project="project"
            :compact="true"
            :hide-description-on-mobile="true"
            :style="{ animationDelay: `${index * 120}ms` }"
            class="animate-fade-in-up min-w-0"
            role="listitem"
          />
        </div>

        <div
          class="hidden min-[1980px]:grid gap-5 xl:gap-6"
          :class="allProjectsGridClasses"
          role="list"
          aria-labelledby="portfolio-heading"
        >
          <PortfolioItem
            v-for="(project, index) in visibleProjects"
            :key="`wide-${project.id || project.title}`"
            :project="project"
            :compact="true"
            :hide-description-on-mobile="true"
            :style="{ animationDelay: `${index * 120}ms` }"
            class="animate-fade-in-up min-w-0"
            role="listitem"
          />
        </div>

        <div v-if="remainingProjectsCount > 0" class="text-center mt-8 sm:mt-10">
          <router-link
            to="/portfolio"
            class="group inline-flex items-center gap-2 px-6 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-blue-600 dark:text-blue-400 font-medium rounded-lg border-2 border-blue-200 dark:border-blue-700/50 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-md"
            aria-label="Alle Projekte ansehen"
          >
            <span class="text-sm sm:text-base">Alle Projekte</span>

            <span
              class="px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full text-xs font-bold"
              :aria-label="`${remainingProjectsCount} weitere Projekte verfügbar`"
            >
              +{{ remainingProjectsCount }}
            </span>

            <svg class="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </router-link>
        </div>
      </div>

      <div v-if="displayProjects.length > 0" class="mt-8 sm:mt-16 lg:mt-20">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="relative p-1 rounded-2xl bg-gradient-to-r from-blue-600/10 via-blue-500/10 to-blue-400/10 dark:from-blue-500/5 dark:via-blue-400/5 dark:to-blue-300/5 backdrop-blur-sm border border-gray-200/50 dark:border-white/10 overflow-hidden group/cta">
            <div class="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover/cta:bg-blue-500/20 transition-colors duration-700"></div>
            <div class="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl group-hover/cta:bg-blue-400/20 transition-colors duration-700"></div>

            <div class="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-xl p-5 sm:p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-8 border border-white/50 dark:border-white/5 shadow-xl shadow-gray-200/50 dark:shadow-black/20">
              <div class="flex-1 text-center sm:text-left">
                <p class="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 tracking-tight">
                  Sie möchten online professioneller auftreten?
                </p>
                <p class="text-xs sm:text-base text-gray-600 dark:text-gray-400 font-light">
                  Dann lassen Sie uns prüfen, welche Website zu Ihrem Angebot passt.
                </p>
              </div>

              <div class="flex-shrink-0 w-full sm:w-auto">
                <router-link
                  to="/contact"
                  class="group/btn relative inline-flex items-center justify-center w-full sm:w-auto px-5 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-500/50 dark:hover:shadow-blue-600/50 transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-300 dark:focus:ring-blue-700 overflow-hidden text-sm sm:text-lg"
                  aria-label="Zur Kontaktseite und Projekt starten"
                >
                  <div class="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-600 dark:from-blue-600 dark:to-blue-700 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 ease-out"></div>
                  <div class="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 ease-in-out bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>

                  <span class="relative z-10">Kostenlose Beratung anfragen</span>
                  <svg class="ml-2 w-4 h-4 sm:w-6 sm:h-6 relative z-10 transition-transform duration-300 group-hover/btn:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in-up {
  opacity: 0;
  animation: fade-in-up 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

@supports (will-change: transform) {
  .animate-fade-in-up {
    will-change: transform, opacity;
  }
}

@media (prefers-reduced-motion: reduce) {
  .animate-fade-in-up {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import PortfolioItem from '../PortfolioItem.vue'
import type { PortfolioProject } from '../../firebase/portfolio'
import { useScrollAnimation } from '../../composables/useScrollAnimation'

const props = defineProps<{
  projects: PortfolioProject[]
  loading: boolean
}>()

const { isVisible, targetElement } = useScrollAnimation({
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
})

const displayProjects = computed(() => {
  return [...props.projects].sort((a, b) => {
    const dateA = a.createdAt?.getTime() || 0
    const dateB = b.createdAt?.getTime() || 0

    if (dateA !== dateB) {
      return dateB - dateA
    }

    if (a.isFavorite !== b.isFavorite) {
      return a.isFavorite ? -1 : 1
    }

    const orderA = a.isFavorite ? a.favoriteOrder || 0 : a.regularOrder || 0
    const orderB = b.isFavorite ? b.favoriteOrder || 0 : b.regularOrder || 0
    return orderA - orderB
  })
})

const visibleProjects = computed(() => {
  return displayProjects.value.slice(0, 4)
})

const remainingProjectsCount = computed(() => {
  return Math.max(0, displayProjects.value.length - visibleProjects.value.length)
})

const favoriteGridClasses = computed(() => {
  const count = visibleProjects.value.length

  if (count <= 1) {
    return 'grid-cols-1 max-w-2xl mx-auto'
  }

  if (count === 2) {
    return 'grid-cols-1 sm:grid-cols-2 max-w-5xl mx-auto'
  }

  if (count === 3) {
    return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
  }

  if (count === 4) {
    return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
  }

  return 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
})

const allProjectsGridClasses = computed(() => {
  return 'min-[1980px]:grid-cols-4'
})
</script>
